import React, { useState, useRef, useCallback } from "react";

// ─── LoginPage ────────────────────────────────────────────────────────────────
// Handles both OTP-based and password-based login.
// Calls onLoginSuccess({ token, uid, userType, name, email, accessType }) when auth succeeds.
// access_type is always sent with every auth request and saved to localStorage.
// ─────────────────────────────────────────────────────────────────────────────

// ── Access types sent with every authentication request ──────────────────────
const ACCESS_TYPE = ["scheduler_access", "billing_access"];
function LoginPage({ onLoginSuccess }) {
  const [step, setStep] = useState("email"); // "email" | "method" | "otp" | "password"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const abortRef = useRef(null);

  const clearMessages = () => {
    setError("");
    setInfo("");
  };

  // ── 1. Send OTP ──────────────────────────────────────────────────────────
  const sendOTP = useCallback(async (emailAddress) => {
    clearMessages();
    setLoading(true);
    abortRef.current = new AbortController();

    try {
      const formdata = new FormData();
      formdata.append("email", emailAddress);

      const response = await fetch(
        "https://erp.iugroup.com/api/send_email_otp",
        { method: "POST", body: formdata, redirect: "follow", signal: abortRef.current.signal }
      );

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid response from server."); }

      if (
        response.status === 404 ||
        data.status_code === 404 ||
        data.msg?.toLowerCase().includes("no user found") ||
        data.message?.toLowerCase().includes("no user found")
      ) {
        setError(data.msg || data.message || "No user found for this email. Please check and try again.");
        setStep("email");
        return false;
      }

      if (!response.ok || data.status === false) {
        throw new Error(data.error || data.msg || data.message || "Unable to send OTP. Please try again.");
      }

      setInfo(`✅ ${data.msg || "OTP sent successfully!"}`);
      if (data.demo_otp) setDemoOtp(data.demo_otp);
      setStep("otp");
      return true;
    } catch (err) {
      if (err.name === "AbortError") return false;
      setError(err.message || "Unable to send OTP. Please try again.");
      return false;
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, []);

  // ── 2. Verify OTP ─────────────────────────────────────────────────────────
  const verifyOTP = useCallback(async () => {
    if (!otp.trim()) { setError("Please enter the OTP."); return; }
    clearMessages();
    setLoading(true);
    abortRef.current = new AbortController();

    try {
      const formdata = new FormData();
      formdata.append("email", email.trim());
      formdata.append("authentication_method", "otp");
      formdata.append("otp", otp.trim());
      formdata.append("password", "");
      formdata.append("access_type", JSON.stringify(ACCESS_TYPE));

      const response = await fetch(
        "https://erp.iugroup.com/api/bot/authentication",
        { method: "POST", body: formdata, redirect: "follow", signal: abortRef.current.signal }
      );

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid server response."); }

      if (
        response.status === 401 ||
        data.status_code === 401 ||
        data.message?.toLowerCase().includes("authentication failed") ||
        data.type?.toLowerCase().includes("authentication failed")
      ) {
        setError("❌ Incorrect OTP. Please verify and try again.");
        setOtp("");
        return;
      }

      if (!response.ok || data.status === false) {
        throw new Error(data.error || "Invalid OTP. Please try again.");
      }

      const token = data.oauth_access_token || data.token || data.access_token || "";
      if (!token) throw new Error("No authentication token received from server.");

      // Use access_type returned by the server; fall back to the hardcoded constant
      const returnedAccessType = data.access_type || ACCESS_TYPE;

      localStorage.setItem("chatAppToken", token);
      localStorage.setItem("chatAppEmail", email.trim());
      localStorage.setItem("chatAppAccessType", JSON.stringify(returnedAccessType));
      onLoginSuccess({ token, uid: data.uid, userType: data.user_type, name: data.name, email: email.trim(), accessType: returnedAccessType });
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [email, otp, onLoginSuccess]);

  // ── 3. Password login ─────────────────────────────────────────────────────
  const handlePasswordLogin = useCallback(async () => {
    if (!passwordInput.trim()) { setError("Please enter your password."); return; }
    clearMessages();
    setLoading(true);
    abortRef.current = new AbortController();

    try {
      const formdata = new FormData();
      formdata.append("email", email.trim());
      formdata.append("authentication_method", "password");
      formdata.append("otp", "");
      formdata.append("password", passwordInput.trim());
      formdata.append("access_type", JSON.stringify(ACCESS_TYPE));

      const response = await fetch(
        "https://erp.iugroup.com/api/bot/authentication",
        { method: "POST", body: formdata, redirect: "follow", signal: abortRef.current.signal }
      );

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Invalid server response."); }

      if (
        response.status === 401 ||
        data.status_code === 401 ||
        data.message?.toLowerCase().includes("authentication failed") ||
        data.type?.toLowerCase().includes("authentication failed")
      ) {
        setError("❌ Incorrect email or password. Please try again.");
        setPasswordInput("");
        return;
      }

      if (!response.ok || data.status === false) {
        throw new Error(data.error || "Invalid credentials. Please try again.");
      }

      const token = data.oauth_access_token || data.token || data.access_token || "";
      if (!token) throw new Error("No authentication token received from server.");

      // Use access_type returned by the server; fall back to the hardcoded constant
      const returnedAccessType = data.access_type || ACCESS_TYPE;

      localStorage.setItem("chatAppToken", token);
      localStorage.setItem("chatAppEmail", email.trim());
      localStorage.setItem("chatAppAccessType", JSON.stringify(returnedAccessType));
      onLoginSuccess({ token, uid: data.uid, userType: data.user_type, name: data.name, email: email.trim(), accessType: returnedAccessType });
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Authentication failed. Please try again.");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [email, passwordInput, onLoginSuccess]);

  // ── Email submit ──────────────────────────────────────────────────────────
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val) {
      setError("Please enter your email or username.");
      return;
    }

    // If it contains '@', treat as email and validate format
    if (val.includes("@")) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        setError("Please enter a valid email address.");
        return;
      }
      clearMessages();
      setStep("method");
    } else {
      // Treat as username (e.g. "admin"), skip directly to password
      clearMessages();
      setStep("password");
    }
  };

  // ── Method selection ──────────────────────────────────────────────────────
  const handleMethodSelect = (method) => {
    clearMessages();
    if (method === "otp") {
      sendOTP(email.trim());
    } else {
      setStep("password");
    }
  };

  const handleBack = () => {
    clearMessages();
    setDemoOtp("");
    setOtp("");
    setPasswordInput("");
    if (step === "method") setStep("email");
    else if (step === "password" && !email.includes("@")) setStep("email");
    else setStep("method");
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        * { box-sizing: border-box; }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(30,91,168,0.4); }
          50%       { box-shadow: 0 0 0 8px rgba(30,91,168,0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .login-card {
          animation: fadeSlideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .login-btn {
          background: linear-gradient(135deg, #1e5ba8 0%, #2d7dd2 100%);
          color: #fff;
          border: none;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          transition: transform 0.18s, box-shadow 0.18s, filter 0.18s;
          letter-spacing: 0.3px;
        }
        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(30,91,168,0.35);
          filter: brightness(1.08);
        }
        .login-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        .login-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .method-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          width: 100%;
          padding: 16px 20px;
          border: 2px solid #e8edf5;
          border-radius: 14px;
          background: #fff;
          cursor: pointer;
          font-size: 15px;
          font-weight: 500;
          color: #1a1a2e;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.15s;
          text-align: left;
        }
        .method-btn:hover {
          border-color: #1e5ba8;
          background: #f0f5ff;
          box-shadow: 0 4px 16px rgba(30,91,168,0.12);
          transform: translateY(-1px);
        }
        .method-btn-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }

        .login-input {
          width: 100%;
          padding: 13px 16px;
          border: 2px solid #e8edf5;
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          color: #1a1a2e;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
        }
        .login-input:focus {
          border-color: #1e5ba8;
          box-shadow: 0 0 0 4px rgba(30,91,168,0.12);
        }
        .login-input::placeholder { color: #9ba8bb; }

        .back-btn {
          background: none;
          border: none;
          color: #1e5ba8;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 4px 0;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }
        .back-btn:hover { opacity: 0.75; }

        .otp-digits {
          display: flex;
          gap: 10px;
          justify-content: center;
        }
        .otp-digit {
          width: 64px;
          height: 64px;
          border: 2px solid #e8edf5;
          border-radius: 12px;
          font-size: 22px;
          font-weight: 700;
          text-align: center;
          color: #1a1a2e;
          background: #fff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-family: 'Inter', sans-serif;
        }
        .otp-digit:focus {
          border-color: #1e5ba8;
          box-shadow: 0 0 0 4px rgba(30,91,168,0.12);
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 3px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
          margin-right: 8px;
          vertical-align: middle;
        }

        .demo-otp-badge {
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border: 1px solid #f59e0b;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          color: #92400e;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
        }

        .resend-link {
          background: none;
          border: none;
          color: #1e5ba8;
          font-size: 13px;
          cursor: pointer;
          text-decoration: underline;
          padding: 0;
          font-family: 'Inter', sans-serif;
        }
        .resend-link:hover { opacity: 0.75; }
      `}</style>

      {/* Left decorative panel */}
      <div style={s.leftPanel}>
        <div style={s.brandMark}>
          <img src="/iu_logo.png" alt="IU Logo" style={s.brandLogo} />
          <div style={s.brandName}>IU AI Assistant</div>
          <div style={s.brandTagline}>Intelligent support at your fingertips</div>
        </div>
        <div style={s.decorCircle1} />
        <div style={s.decorCircle2} />
        <div style={s.decorCircle3} />
      </div>

      {/* Right form panel */}
      <div style={s.rightPanel}>
        <div className="login-card" style={s.card}>
          {/* Logo (mobile only fallback) */}
          <div style={s.mobileLogoRow}>
            <img src="/iu_logo.png" alt="IU" style={{ width: 36, height: 36, borderRadius: "50%" }} />
            <span style={{ fontWeight: 700, fontSize: 18, color: "#1e5ba8" }}>IU AI Assistant</span>
          </div>

          {/* ── Email step ── */}
          {step === "email" && (
            <>
              <div style={s.stepHeader}>
                <div style={s.stepIcon}>✉️</div>
                <h2 style={s.title}>Welcome back</h2>
                <p style={s.subtitle}>Enter your email or username to continue</p>
              </div>
              <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={s.label}>Email or Username</label>
                  <input
                    className="login-input"
                    type="text"
                    placeholder="you@example.com or username"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                    autoFocus
                    required
                  />
                </div>
                {error && <div style={s.errorBox}>⚠️ {error}</div>}
                <button className="login-btn" type="submit" disabled={loading || !email.trim()}>
                  Continue →
                </button>
              </form>
            </>
          )}

          {/* ── Method step ── */}
          {step === "method" && (
            <>
              <div style={s.stepHeader}>
                <div style={s.stepIcon}>🔐</div>
                <h2 style={s.title}>Choose sign-in method</h2>
                <p style={s.subtitle}>How would you like to sign in as <strong>{email}</strong>?</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <button className="method-btn" onClick={() => handleMethodSelect("otp")} disabled={loading}>
                  <div className="method-btn-icon" style={{ background: "linear-gradient(135deg, #dbeafe, #bfdbfe)" }}>📧</div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Sign in with OTP</div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>We'll send a one-time code to your email</div>
                  </div>
                </button>
                <button className="method-btn" onClick={() => handleMethodSelect("password")} disabled={loading}>
                  <div className="method-btn-icon" style={{ background: "linear-gradient(135deg, #d1fae5, #a7f3d0)" }}>🔑</div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Sign in with Password</div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>Use your account password</div>
                  </div>
                </button>
              </div>
              {loading && <div style={s.loadingRow}><span className="spinner" style={s.spinnerDark} />Sending OTP…</div>}
              {error && <div style={s.errorBox}>⚠️ {error}</div>}
              <button className="back-btn" style={{ marginTop: 12 }} onClick={handleBack}>← Back</button>
            </>
          )}

          {/* ── OTP step ── */}
          {step === "otp" && (
            <>
              <div style={s.stepHeader}>
                <div style={s.stepIcon}>📨</div>
                <h2 style={s.title}>Check your email</h2>
                <p style={s.subtitle}>Enter the 4-digit code sent to <strong>{email}</strong></p>
              </div>

              <OtpInput value={otp} onChange={setOtp} />

              {demoOtp && (
                <div className="demo-otp-badge">
                  🛠️ <span>Demo OTP: <strong>{demoOtp}</strong></span>
                </div>
              )}
              {info && <div style={s.infoBox}>{info}</div>}
              {error && <div style={s.errorBox}>⚠️ {error}</div>}

              <button
                className="login-btn"
                style={{ marginTop: 8 }}
                onClick={verifyOTP}
                disabled={loading || otp.replace(/\s/g, "").length < 4}
              >
                {loading ? <><span className="spinner" />Verifying…</> : "Verify OTP"}
              </button>

              <div style={{ textAlign: "center", fontSize: 13, color: "#6b7280", marginTop: 10 }}>
                Didn't receive it?{" "}
                <button className="resend-link" onClick={() => { setOtp(""); setDemoOtp(""); sendOTP(email.trim()); }} disabled={loading}>
                  Resend OTP
                </button>
              </div>
              <button className="back-btn" style={{ marginTop: 6 }} onClick={handleBack}>← Back</button>
            </>
          )}

          {/* ── Password step ── */}
          {step === "password" && (
            <>
              <div style={s.stepHeader}>
                <div style={s.stepIcon}>🔑</div>
                <h2 style={s.title}>Enter your password</h2>
                <p style={s.subtitle}>Sign in as <strong>{email}</strong></p>
              </div>

              <div>
                <label style={s.label}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    className="login-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Your password"
                    value={passwordInput}
                    onChange={(e) => { setPasswordInput(e.target.value); clearMessages(); }}
                    onKeyDown={(e) => e.key === "Enter" && !loading && handlePasswordLogin()}
                    autoFocus
                    style={{ paddingRight: 48 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    style={s.eyeBtn}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              {error && <div style={s.errorBox}>⚠️ {error}</div>}

              <button
                className="login-btn"
                style={{ marginTop: 4 }}
                onClick={handlePasswordLogin}
                disabled={loading || !passwordInput.trim()}
              >
                {loading ? <><span className="spinner" />Signing in…</> : "Sign In"}
              </button>

              <button className="back-btn" style={{ marginTop: 8 }} onClick={handleBack}>← Back</button>
            </>
          )}
        </div>

        <p style={s.footer}>© {new Date().getFullYear()} IU Group · Secure Login</p>
      </div>
    </div>
  );
}

// ─── OTP 6-digit input ────────────────────────────────────────────────────────
function OtpInput({ value, onChange }) {
  const digits = 4;
  const refs = Array.from({ length: digits }, () => useRef(null)); // eslint-disable-line react-hooks/rules-of-hooks

  const vals = value.replace(/\D/g, "").split("").slice(0, digits);
  while (vals.length < digits) vals.push("");

  const handleChange = (idx, e) => {
    const char = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...vals];
    next[idx] = char;
    onChange(next.join(""));
    if (char && idx < digits - 1) refs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (!vals[idx] && idx > 0) {
        refs[idx - 1].current?.focus();
        const next = [...vals];
        next[idx - 1] = "";
        onChange(next.join(""));
      } else {
        const next = [...vals];
        next[idx] = "";
        onChange(next.join(""));
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs[idx - 1].current?.focus();
    } else if (e.key === "ArrowRight" && idx < digits - 1) {
      refs[idx + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, digits);
    if (pasted) {
      onChange(pasted.padEnd(digits, "").slice(0, digits));
      const focusIdx = Math.min(pasted.length, digits - 1);
      refs[focusIdx].current?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="otp-digits">
      {vals.map((v, i) => (
        <input
          key={i}
          ref={refs[i]}
          className="otp-digit"
          inputMode="numeric"
          maxLength={1}
          value={v}
          autoFocus={i === 0}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page: {
    display: "flex",
    minHeight: "100vh",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    background: "#f8faff",
  },
  leftPanel: {
    flex: "0 0 42%",
    background: "linear-gradient(145deg, #0f2d6b 0%, #1e5ba8 50%, #2d7dd2 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 48px",
    position: "relative",
    overflow: "hidden",
    "@media(maxWidth:768px)": { display: "none" },
  },
  brandMark: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    zIndex: 1,
    textAlign: "center",
  },
  brandLogo: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    border: "4px solid rgba(255,255,255,0.3)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  },
  brandName: {
    fontSize: 32,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: "-0.5px",
  },
  brandTagline: {
    fontSize: 16,
    color: "rgba(255,255,255,0.75)",
    maxWidth: 260,
    lineHeight: 1.6,
  },
  decorCircle1: {
    position: "absolute", top: -80, right: -80,
    width: 320, height: 320, borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
  },
  decorCircle2: {
    position: "absolute", bottom: -60, left: -60,
    width: 260, height: 260, borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
  },
  decorCircle3: {
    position: "absolute", bottom: 120, right: 40,
    width: 140, height: 140, borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
  },
  rightPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 24px",
    minHeight: "100vh",
  },
  card: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 8px 40px rgba(30,91,168,0.12)",
    padding: "40px 36px",
    width: "100%",
    maxWidth: 440,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  mobileLogoRow: {
    display: "none",
  },
  stepHeader: {
    textAlign: "center",
    marginBottom: 4,
  },
  stepIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: "#0f1f3d",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "8px 0 0",
    fontSize: 14.5,
    color: "#6b7280",
    lineHeight: 1.5,
  },
  label: {
    display: "block",
    fontSize: 13.5,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13.5,
    lineHeight: 1.5,
  },
  infoBox: {
    background: "#f0fdf4",
    border: "1px solid #86efac",
    color: "#166534",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13.5,
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#6b7280",
    justifyContent: "center",
    marginTop: 4,
  },
  spinnerDark: {
    borderColor: "rgba(30,91,168,0.2)",
    borderTopColor: "#1e5ba8",
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 18,
    padding: 4,
    lineHeight: 1,
  },
  footer: {
    marginTop: 24,
    fontSize: 12.5,
    color: "#9ca3af",
    textAlign: "center",
  },
};

export default LoginPage;
